import 'react-swipeable-list/dist/styles.css'

import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Download, Info, ListX, Music, Repeat, Repeat1, Shuffle, Trash2, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import { SwipeableList, SwipeableListItem, SwipeAction, TrailingActions } from 'react-swipeable-list'
import { toast } from 'sonner'

import { DownloadProgress } from '@/components/shared/download-progress'
import { DownloadUrlForm } from '@/components/shared/download-url-form'
import { Header } from '@/components/shared/header'
import { StorageUsage } from '@/components/shared/storage-usage'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { useGoBack } from '@/hooks/useGoBack'
import { useOnlineStatus } from '@/hooks/useOnlineStatus'
import { db, type MusicPlaylist, type StoredExternalMusic } from '@/lib/db'
import { formatDuration } from '@/lib/time-format'
import { formatBytes } from '@/lib/utils'

import { useExternalMusicDownload } from './hooks/useExternalMusicDownload'
import { useMediaSession } from './hooks/useMediaSession'
import { usePlaylist } from './hooks/usePlaylist'
import { MusicInfoDialog } from './music-info-dialog'
import { PlaylistChips } from './playlist-chips'
import { PlaylistCreateDialog } from './playlist-create-dialog'
import { PlaylistEditSheet } from './playlist-edit-sheet'

type SortableMusicItemProps = {
  music: StoredExternalMusic
  isPlaying: boolean
  isPlaylistView: boolean
  onSelect: (music: StoredExternalMusic) => void
  onInfo: (music: StoredExternalMusic) => void
  onDelete: (music: StoredExternalMusic) => void
  showSeparator: boolean
}

const useThumbnailUrl = (blob: Blob | null) => {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!blob) {
      setUrl(null)
      return
    }
    const objectUrl = URL.createObjectURL(blob)
    setUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [blob])
  return url
}

const ThumbnailImage = ({ blob, alt, className }: { blob: Blob; alt: string; className?: string }) => {
  const url = useThumbnailUrl(blob)
  if (!url) return null
  return <img src={url} alt={alt} className={className} />
}

/** サムネイル or Music アイコンのフォールバック付き表示 */
const MusicThumbnail = ({
  blob,
  alt,
  size = 'md',
  children,
}: {
  blob: Blob | null
  alt: string
  /** md: 56px (リスト), sm: 40px (フッター) */
  size?: 'sm' | 'md'
  /** オーバーレイ要素（再生中インジケーター等） */
  children?: React.ReactNode
}) => {
  const sizeClass = size === 'sm' ? 'w-10 h-10' : 'w-14 h-14'
  const iconSize = size === 'sm' ? 16 : 24

  return (
    <div className={`relative ${sizeClass} shrink-0 bg-muted rounded overflow-hidden`}>
      {blob ? (
        <ThumbnailImage blob={blob} alt={alt} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Music size={iconSize} className="text-muted-foreground" />
        </div>
      )}
      {children}
    </div>
  )
}

const SortableMusicItem = ({
  music,
  isPlaying,
  isPlaylistView,
  onSelect,
  onInfo,
  onDelete,
  showSeparator,
}: SortableMusicItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: music.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const trailingActions = (
    <TrailingActions>
      <SwipeAction onClick={() => onDelete(music)}>
        <div className="flex flex-col items-center justify-center gap-1 px-4 bg-red-500 text-white h-full">
          {isPlaylistView ? <ListX size={20} /> : <Trash2 size={20} />}
          <span className="text-xs">{isPlaylistView ? '除外' : '削除'}</span>
        </div>
      </SwipeAction>
    </TrailingActions>
  )

  return (
    <div ref={setNodeRef} style={style} className="w-full my-1">
      <SwipeableListItem
        trailingActions={trailingActions}
        className="w-full"
        threshold={0.3}
        fullSwipe={false}
        blockSwipe={false}
      >
        <div className="flex items-center cursor-pointer w-full">
          {/* ドラッグハンドル */}
          <div {...attributes} {...listeners} className="shrink-0 px-2 cursor-grab active:cursor-grabbing">
            <div className="flex flex-col gap-1">
              <div className="w-1 h-1 rounded-full bg-muted-foreground" />
              <div className="w-1 h-1 rounded-full bg-muted-foreground" />
              <div className="w-1 h-1 rounded-full bg-muted-foreground" />
            </div>
          </div>

          <div onClick={() => onSelect(music)} className="flex items-center gap-3 flex-1 min-w-0">
            {/* サムネイル */}
            <MusicThumbnail blob={music.thumbnailBlob} alt={music.title}>
              {/* 再生中インジケーター */}
              {isPlaying && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <div className="flex gap-0.5 items-end h-4">
                    <div
                      className="w-1 bg-white rounded-full animate-bounce"
                      style={{ height: '60%', animationDelay: '0ms' }}
                    />
                    <div
                      className="w-1 bg-white rounded-full animate-bounce"
                      style={{ height: '100%', animationDelay: '150ms' }}
                    />
                    <div
                      className="w-1 bg-white rounded-full animate-bounce"
                      style={{ height: '40%', animationDelay: '300ms' }}
                    />
                  </div>
                </div>
              )}
            </MusicThumbnail>

            {/* 音楽情報 */}
            <div className="flex-1 min-w-0">
              <h3 className="font-medium truncate">{music.title}</h3>
              <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                {music.artist && <span className="truncate">{music.artist}</span>}
                {music.duration != null && <span>{formatDuration(music.duration)}</span>}
                {music.fileSize > 0 && <span>{formatBytes(music.fileSize)}</span>}
              </div>
            </div>
          </div>

          {/* 情報ボタン */}
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0"
            onClick={(e) => {
              e.stopPropagation()
              onInfo(music)
            }}
          >
            <Info size={20} />
          </Button>
        </div>
      </SwipeableListItem>
      {showSeparator && <Separator className="mt-1" />}
    </div>
  )
}

type PlayMode = 'normal' | 'repeat-all' | 'repeat-one' | 'shuffle'

export const CustomMusic = () => {
  const goBack = useGoBack()
  const [searchParams, setSearchParams] = useSearchParams()
  const [url, setUrl] = useState(() => searchParams.get('url') ?? '')
  const [musicList, setMusicList] = useState<StoredExternalMusic[]>([])
  const [selectedMusic, setSelectedMusic] = useState<StoredExternalMusic | null>(null)
  const [totalSize, setTotalSize] = useState(0)
  const [infoMusic, setInfoMusic] = useState<StoredExternalMusic | null>(null)
  const [playMode, setPlayMode] = useState<PlayMode>('normal')
  const [activePlaylistId, setActivePlaylistId] = useState<string | null>(null)
  const [playbackScope, setPlaybackScope] = useState<string | null>(null)
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [editingPlaylist, setEditingPlaylist] = useState<MusicPlaylist | null>(null)

  const audioRef = useRef<HTMLAudioElement>(null)

  const { downloadState, downloadMusic, cancelDownload } = useExternalMusicDownload()
  const { isOnline } = useOnlineStatus()

  // URLパラメータからURLを読み取った後、クエリパラメータをクリーンアップ
  useEffect(() => {
    if (searchParams.has('url')) {
      setSearchParams(new URLSearchParams(), { replace: true })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const {
    playlists,
    createPlaylist,
    renamePlaylist,
    deletePlaylist,
    addMusicToPlaylist,
    removeMusicFromPlaylist,
    reorderPlaylistMusic,
    getPlaylistIdsForMusic,
  } = usePlaylist()

  /** アクティブプレイリストでフィルタした曲リスト */
  const filteredMusicList = useMemo(() => {
    if (!activePlaylistId) return musicList
    const playlist = playlists.find((p) => p.id === activePlaylistId)
    if (!playlist) return musicList
    // プレイリスト内の musicIds 順で返す
    return playlist.musicIds
      .map((id) => musicList.find((m) => m.id === id))
      .filter((m): m is StoredExternalMusic => !!m)
  }, [musicList, activePlaylistId, playlists])

  /** 再生スコープに基づく曲リスト（シャッフル/リピート/前後の曲はこのリストから選ぶ） */
  const playbackScopedList = useMemo(() => {
    if (!playbackScope) return musicList
    const playlist = playlists.find((p) => p.id === playbackScope)
    if (!playlist) return musicList
    return playlist.musicIds
      .map((id) => musicList.find((m) => m.id === id))
      .filter((m): m is StoredExternalMusic => !!m)
  }, [musicList, playbackScope, playlists])

  /** 情報ダイアログに表示中の曲が所属するプレイリストID */
  const infoMusicPlaylistIds = useMemo(() => {
    if (!infoMusic) return new Set<string>()
    return getPlaylistIdsForMusic(infoMusic.id)
  }, [infoMusic, getPlaylistIdsForMusic])

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  // 保存された音楽のリストを取得
  const loadMusic = useCallback(async () => {
    let allMusic = await db.externalMusic.toArray()

    // マイグレーション: orderフィールドがない音楽に追加
    const needsMigration = allMusic.some((m) => m.order === undefined)
    if (needsMigration) {
      await Promise.all(
        allMusic.map((m, index) => {
          if (m.order === undefined) {
            return db.externalMusic.update(m.id, { order: index })
          }
          return Promise.resolve()
        }),
      )
      allMusic = await db.externalMusic.toArray()
    }

    // orderでソート
    allMusic.sort((a, b) => a.order - b.order)
    setMusicList(allMusic)

    // 合計容量を計算
    const total = allMusic.reduce((sum, m) => sum + (m.fileSize || 0), 0)
    setTotalSize(total)
  }, [])

  useEffect(() => {
    loadMusic()
  }, [loadMusic])

  // ダウンロード完了時にリストを更新
  useEffect(() => {
    if (downloadState.status === 'completed') {
      loadMusic()
      setUrl('')
    }
  }, [downloadState.status, loadMusic])

  const handleDownload = useCallback(
    (e?: React.MouseEvent) => {
      e?.preventDefault()
      e?.stopPropagation()

      if (downloadState.isDownloading) {
        return
      }
      if (!url.trim()) {
        return
      }

      downloadMusic(url)
    },
    [url, downloadMusic, downloadState.isDownloading],
  )

  const handleSelect = useCallback(
    (music: StoredExternalMusic) => {
      setSelectedMusic((prev) => {
        if (prev?.id === music.id) {
          // 同じ曲をタップした場合は選択解除
          return null
        }
        // 再生開始時のスコープを記録
        setPlaybackScope(activePlaylistId)
        return music
      })
    },
    [activePlaylistId],
  )

  const handleDelete = async (music: StoredExternalMusic) => {
    // プレイリスト表示中はプレイリストから除外
    if (activePlaylistId) {
      await removeMusicFromPlaylist(activePlaylistId, music.id)
      toast.success('プレイリストから除外しました')
      return
    }

    try {
      await db.externalMusic.delete(music.id)
      toast.success('音楽を削除しました')
      loadMusic()
      if (selectedMusic?.id === music.id) {
        setSelectedMusic(null)
      }
    } catch (error) {
      console.error('Failed to delete music:', error)
      toast.error('削除に失敗しました')
    }
  }

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) return

      const list = filteredMusicList
      const oldIndex = list.findIndex((m) => m.id === active.id)
      const newIndex = list.findIndex((m) => m.id === over.id)
      const newList = arrayMove(list, oldIndex, newIndex)

      if (activePlaylistId) {
        // プレイリスト内の並び替え
        try {
          await reorderPlaylistMusic(
            activePlaylistId,
            newList.map((m) => m.id),
          )
        } catch (error) {
          console.error('Failed to reorder playlist:', error)
          toast.error('並び順の更新に失敗しました')
        }
        return
      }

      // 全曲リストの並び替え
      setMusicList(newList)
      try {
        await Promise.all(newList.map((m, index) => db.externalMusic.update(m.id, { order: index })))
      } catch (error) {
        console.error('Failed to update order:', error)
        toast.error('並び順の更新に失敗しました')
        loadMusic()
      }
    },
    [filteredMusicList, activePlaylistId, reorderPlaylistMusic, loadMusic],
  )

  // 選択された音楽のObject URLを生成
  const audioUrl = useMemo(() => {
    if (!selectedMusic) return null
    return URL.createObjectURL(selectedMusic.audioBlob)
  }, [selectedMusic])

  // クリーンアップ: Object URLを解放
  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl)
      }
    }
  }, [audioUrl])

  // シャッフル再生: 現在の曲以外からランダムに選択
  const pickRandomTrack = useCallback(() => {
    if (!selectedMusic) return
    const others = playbackScopedList.filter((m) => m.id !== selectedMusic.id)
    if (others.length === 0) return
    const random = others.at(Math.floor(Math.random() * others.length))
    setSelectedMusic(random ?? null)
  }, [selectedMusic, playbackScopedList])

  // 前の曲を再生
  const handlePreviousTrack = useCallback(() => {
    if (!selectedMusic || playbackScopedList.length === 0) return

    if (playMode === 'shuffle') {
      pickRandomTrack()
      return
    }

    const currentIndex = playbackScopedList.findIndex((m) => m.id === selectedMusic.id)
    if (currentIndex > 0) {
      setSelectedMusic(playbackScopedList.at(currentIndex - 1) ?? null)
    } else if (playMode === 'repeat-all') {
      setSelectedMusic(playbackScopedList.at(-1) ?? null)
    }
  }, [selectedMusic, playbackScopedList, playMode, pickRandomTrack])

  // 次の曲を再生
  const handleNextTrack = useCallback(() => {
    if (!selectedMusic || playbackScopedList.length === 0) return

    if (playMode === 'shuffle') {
      pickRandomTrack()
      return
    }

    const currentIndex = playbackScopedList.findIndex((m) => m.id === selectedMusic.id)
    if (currentIndex < playbackScopedList.length - 1) {
      setSelectedMusic(playbackScopedList.at(currentIndex + 1) ?? null)
    } else if (playMode === 'repeat-all') {
      setSelectedMusic(playbackScopedList.at(0) ?? null)
    }
  }, [selectedMusic, playbackScopedList, playMode, pickRandomTrack])

  // 曲終了時のハンドラ
  const handleEnded = useCallback(() => {
    if (playMode === 'repeat-one') {
      // 1曲リピート: 先頭に戻して再生
      const audio = audioRef.current
      if (audio) {
        audio.currentTime = 0
        audio.play()
      }
      return
    }
    handleNextTrack()
  }, [playMode, handleNextTrack, audioRef])

  // 再生モード切り替え: normal → repeat-all → repeat-one → shuffle → normal
  const cyclePlayMode = useCallback(() => {
    setPlayMode((prev) => {
      const modes: PlayMode[] = ['normal', 'repeat-all', 'repeat-one', 'shuffle']
      const currentIdx = modes.indexOf(prev)
      return modes.at((currentIdx + 1) % modes.length) ?? 'normal'
    })
  }, [])

  // Media Session API（Android バックグラウンド再生 + 通知コントロール）
  useMediaSession({
    music: selectedMusic,
    audioRef,
    onPreviousTrack: handlePreviousTrack,
    onNextTrack: handleNextTrack,
  })

  return (
    <div className="flex flex-col h-full overflow-x-hidden">
      <Header title="カスタム音楽" onClose={goBack} />
      <div className="flex flex-col flex-1 overflow-hidden">
        <div className="flex flex-col flex-1 overflow-hidden px-4 pt-4 gap-4">
          {isOnline && (
            <>
              {/* ダウンロードフォーム */}
              <div className="space-y-2">
                <DownloadUrlForm
                  url={url}
                  onUrlChange={setUrl}
                  onDownload={handleDownload}
                  onCancel={cancelDownload}
                  isDownloading={downloadState.isDownloading}
                  placeholder="YouTubeのURLを入力（音声のみ保存）"
                />

                {/* ダウンロード進捗 */}
                {downloadState.isDownloading && (
                  <DownloadProgress
                    message={downloadState.message}
                    progress={downloadState.progress}
                    speed={downloadState.speed}
                    eta={downloadState.eta}
                  />
                )}
              </div>
            </>
          )}

          {/* 合計容量 */}
          <StorageUsage totalSize={totalSize} />

          {/* プレイリストチップバー */}
          {(playlists.length > 0 || musicList.length > 0) && (
            <PlaylistChips
              playlists={playlists}
              activePlaylistId={activePlaylistId}
              onSelect={setActivePlaylistId}
              onCreate={() => setShowCreateDialog(true)}
              onEdit={setEditingPlaylist}
            />
          )}

          {/* 保存された音楽リスト */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden w-full">
            {filteredMusicList.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                <Download size={48} className="mb-2" />
                <p>保存された音楽がありません</p>
              </div>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={filteredMusicList.map((m) => m.id)} strategy={verticalListSortingStrategy}>
                  <SwipeableList className="w-full">
                    {filteredMusicList.map((music, index) => (
                      <SortableMusicItem
                        key={music.id}
                        music={music}
                        isPlaying={selectedMusic?.id === music.id}
                        isPlaylistView={!!activePlaylistId}
                        onSelect={handleSelect}
                        onInfo={setInfoMusic}
                        onDelete={handleDelete}
                        showSeparator={index < filteredMusicList.length - 1}
                      />
                    ))}
                  </SwipeableList>
                </SortableContext>
              </DndContext>
            )}
          </div>
        </div>
        {/* オーディオプレイヤー（固定フッター） */}
        {selectedMusic && audioUrl && (
          <div className="bg-background border-t p-3 px-4">
            <div className="flex items-center gap-3 mb-2">
              {/* サムネイル */}
              <MusicThumbnail blob={selectedMusic.thumbnailBlob} alt={selectedMusic.title} size="sm" />

              {/* 曲情報 */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{selectedMusic.title}</p>
                {selectedMusic.artist && (
                  <p className="text-xs text-muted-foreground truncate">{selectedMusic.artist}</p>
                )}
              </div>

              {/* 再生モードボタン */}
              <Button variant="ghost" size="icon" className="shrink-0" onClick={cyclePlayMode}>
                {playMode === 'shuffle' && <Shuffle size={18} className="text-primary" />}
                {playMode === 'repeat-one' && <Repeat1 size={18} className="text-primary" />}
                {playMode === 'repeat-all' && <Repeat size={18} className="text-primary" />}
                {playMode === 'normal' && <Repeat size={18} className="text-muted-foreground" />}
              </Button>

              {/* 閉じるボタン */}
              <Button variant="ghost" size="icon" className="shrink-0" onClick={() => setSelectedMusic(null)}>
                <X size={18} />
              </Button>
            </div>

            {/* HTML5オーディオプレイヤー */}
            <audio ref={audioRef} src={audioUrl} controls autoPlay onEnded={handleEnded} className="w-full h-10" />
          </div>
        )}

        {/* 音楽情報ダイアログ */}
        <MusicInfoDialog
          music={infoMusic}
          onClose={() => setInfoMusic(null)}
          onSaved={loadMusic}
          playlists={playlists}
          memberPlaylistIds={infoMusicPlaylistIds}
          onTogglePlaylist={(playlistId, musicId, checked) => {
            if (checked) {
              addMusicToPlaylist(playlistId, musicId)
            } else {
              removeMusicFromPlaylist(playlistId, musicId)
            }
          }}
        />

        {/* プレイリスト新規作成ダイアログ */}
        <PlaylistCreateDialog
          open={showCreateDialog}
          onClose={() => setShowCreateDialog(false)}
          onCreate={createPlaylist}
        />

        {/* プレイリスト編集シート */}
        <PlaylistEditSheet
          playlist={editingPlaylist}
          onClose={() => setEditingPlaylist(null)}
          onRename={renamePlaylist}
          onDelete={async (id) => {
            await deletePlaylist(id)
            if (activePlaylistId === id) {
              setActivePlaylistId(null)
            }
          }}
        />
      </div>
    </div>
  )
}
